// src/pages/Tools/ToolsPage.tsx
// Deep-dive rewrite: DeepDiveSection cards for every tool — matching FeaturesPage design language

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import { seoConfig } from '../../config/seo';
import {
    FileText, FileDown, Combine, QrCode, Scan, Type, Image, FileJson,
    Shield, CheckCircle, ArrowRight, LucideIcon, Star,
} from 'lucide-react';
import { m } from 'framer-motion';
import { OptimizedImage } from '@/components/ui/optimized-image';
import { Typography } from '@/components/ui/typography';
import { allPosts } from '../Insights/Articles/ArticleData';
import { Article } from '../Insights/types';

// ── Types ────────────────────────────────────────────────────────────────────

interface DeepDiveCard {
    id: string;
    icon: LucideIcon;
    title: string;
    tagline: string;
    description: string;
    useCases: string[];
    keyFacts: { label: string; value: string }[];
    link: string;
    cta: string;
    accentColor: string;
    accentBg: string;
}

// ── Tool Data ────────────────────────────────────────────────────────────────

const toolCards: DeepDiveCard[] = [
    {
        id: 'text-compare',
        icon: FileText,
        title: 'Text Comparison Tool',
        tagline: 'The most private diff tool on the web',
        description:
            'Compare two documents, code snippets, or data files with character-level precision — entirely in your browser. The intelligent diff engine highlights every addition, deletion, and modification with color-coded overlays. Used daily by developers for code review, lawyers for contract revisions, and editors for proofreading.',
        useCases: [
            'Review pull request diffs without uploading code to a third party',
            'Compare two versions of a legal contract before signing',
            'Proofread content edits — see exactly what changed between drafts',
            'Audit configuration files for accidental changes',
        ],
        keyFacts: [
            { label: 'Precision', value: 'Character-level' },
            { label: 'Privacy', value: '100% local' },
            { label: 'File size', value: 'Unlimited' },
        ],
        link: '/tools/compare-text',
        cta: 'Open Text Compare',
        accentColor: '#1a73e8',
        accentBg: '#e8f0fe',
    },
    {
        id: 'file-reducer',
        icon: FileDown,
        title: 'File Size Reducer',
        tagline: 'Compress up to 80% — quality stays',
        description:
            'Reduce PDF, image, and document file sizes using adaptive compression algorithms that analyse each file type and apply optimal settings automatically. No fixed quality presets — the engine targets the best size/quality balance for your specific file. Ideal before emailing, uploading to a portal, or archiving.',
        useCases: [
            'Reduce a scanned PDF from 15 MB to under 2 MB for email',
            'Optimise product photos for fast page loads without visible loss',
            'Compress Word documents before submitting to an online portal',
            'Batch-reduce a folder of images for storage savings',
        ],
        keyFacts: [
            { label: 'Max reduction', value: '~80%' },
            { label: 'Formats', value: 'PDF · JPG · PNG · DOCX' },
            { label: 'Mode', value: 'Auto + manual' },
        ],
        link: '/tools/compress-pdf-images',
        cta: 'Compress Files',
        accentColor: '#1e8e3e',
        accentBg: '#e6f4ea',
    },
    {
        id: 'merge-split',
        icon: Combine,
        title: 'PDF Merge & Split',
        tagline: 'Full page-level control over your PDFs',
        description:
            'Merge multiple PDF files into a single ordered document, or split a large PDF into precise page ranges. Drag-and-drop reordering lets you set the exact page sequence before merging. Extract just the pages you need from a 200-page report. Maintain vector quality throughout — no re-rendering, no resolution loss.',
        useCases: [
            'Consolidate monthly reports into a single annual document',
            'Extract one chapter from a large ebook or manual',
            'Merge scanned receipts into one file for expense reporting',
            'Split a large form pack into individual client files',
        ],
        keyFacts: [
            { label: 'Operation', value: 'Merge & split' },
            { label: 'Page control', value: 'Drag to reorder' },
            { label: 'Quality', value: 'Vector preserved' },
        ],
        link: '/tools/merge-split-pdf',
        cta: 'Merge or Split PDF',
        accentColor: '#9334e6',
        accentBg: '#f3e8fd',
    },
    {
        id: 'qr-generator',
        icon: QrCode,
        title: 'QR Code Generator',
        tagline: 'Custom QR codes with brand identity',
        description:
            'Generate fully customised QR codes for any URL, plain text, WiFi credentials, or contact card. Embed your logo, set brand colours, choose error-correction level, and export high-resolution PNG or crisp SVG. QR codes never expire — there is no server to go down because the code itself encodes all the data.',
        useCases: [
            'Add QR codes to business cards that link to your portfolio',
            'Print WiFi credentials on a card for guests — no password sharing',
            'Put a QR code on product packaging to link to a how-to video',
            'Generate event ticket QR codes for check-ins',
        ],
        keyFacts: [
            { label: 'Output', value: 'PNG · SVG' },
            { label: 'Logo embed', value: 'Supported' },
            { label: 'Expiry', value: 'Never' },
        ],
        link: '/tools/qr-code-generator',
        cta: 'Generate QR Code',
        accentColor: '#f9ab00',
        accentBg: '#fef7e0',
    },
    {
        id: 'ocr',
        icon: Type,
        title: 'OCR Text Extractor',
        tagline: 'AI text recognition across 13 languages',
        description:
            'Extract editable, searchable text from any image, screenshot, or scanned document using an advanced AI recognition engine. Handles printed text with near-perfect accuracy and has strong support for handwriting. Processes multi-page PDFs page by page. Outputs clean, formatted text ready for editing or search indexing.',
        useCases: [
            'Extract text from a scanned contract so you can search and edit it',
            'Digitise lecture notes from a whiteboard photo',
            'Convert a printed form into editable data without retyping',
            'Pull product information from a photo of a receipt or label',
        ],
        keyFacts: [
            { label: 'Languages', value: '13+' },
            { label: 'Handwriting', value: 'Supported' },
            { label: 'Multi-page', value: 'PDF supported' },
        ],
        link: '/tools/scan-image-to-text',
        cta: 'Extract Text',
        accentColor: '#1a73e8',
        accentBg: '#e8f0fe',
    },
    {
        id: 'bg-remover',
        icon: Image,
        title: 'AI Background Remover',
        tagline: 'Professional-grade cutouts in one click',
        description:
            'Remove backgrounds automatically using industrial-grade AI edge detection that distinguishes subjects from complex backgrounds — hair, fur, and fine edges included. Produces clean transparent PNGs immediately. No manual masking, no Photoshop required. Outputs are ready for product listings, presentations, or composite designs.',
        useCases: [
            'Create clean product images for an e-commerce listing',
            'Make profile photos with a transparent or custom background',
            'Prepare assets for a presentation without a photo editor',
            'Design marketing banners by compositing subjects onto new backgrounds',
        ],
        keyFacts: [
            { label: 'Output', value: 'Transparent PNG' },
            { label: 'Edge quality', value: 'AI hair & fur' },
            { label: 'Skill needed', value: 'None' },
        ],
        link: '/tools/remove-background',
        cta: 'Remove Background',
        accentColor: '#1e8e3e',
        accentBg: '#e6f4ea',
    },
    {
        id: 'barcode-scanner',
        icon: Scan,
        title: 'Barcode & QR Scanner',
        tagline: 'Decode any code — camera or image upload',
        description:
            'Decode barcodes and QR codes directly from your browser using the device camera in real time or by uploading an image file. Supports every major format used in retail, logistics, and events. No app to install, no hardware needed beyond a phone or webcam. Decoded results can be copied, exported, or followed as a link.',
        useCases: [
            'Look up a product from its barcode while shopping',
            'Scan event tickets for check-ins from any browser',
            'Decode a QR code from a screenshot without a phone',
            'Verify a printed barcode reads correctly before going to print',
        ],
        keyFacts: [
            { label: 'Formats', value: 'QR · EAN · UPC · Code 128' },
            { label: 'Input', value: 'Camera or image' },
            { label: 'App needed', value: 'Never' },
        ],
        link: '/tools/barcode-scanner',
        cta: 'Scan a Code',
        accentColor: '#e37400',
        accentBg: '#fef0d6',
    },
    {
        id: 'json-validator',
        icon: FileJson,
        title: 'JSON Validator & Power Tool',
        tagline: 'Real-time validation with smart auto-repair',
        description:
            'Validate, format, and query JSON data in real-time with a smart engine that detects syntax errors and offers auto-repair suggestions. Run JSONPath queries to extract nested values, validate against a schema, and format output with configurable indentation. Everything stays in your browser — no data leaves your device.',
        useCases: [
            'Debug a malformed API response without copying it into Postman',
            'Format minified JSON from a production log for human reading',
            'Validate a config file against a schema before deploying',
            'Extract specific nested fields from a large JSON payload instantly',
        ],
        keyFacts: [
            { label: 'Validation', value: 'Real-time' },
            { label: 'Auto-repair', value: 'Smart fix' },
            { label: 'Privacy', value: 'Local only' },
        ],
        link: '/tools/json-validator-formatter',
        cta: 'Open JSON Tool',
        accentColor: '#9334e6',
        accentBg: '#f3e8fd',
    },
];

// ── DeepDive Card Component ───────────────────────────────────────────────────

const DeepDiveSection: React.FC<{ card: DeepDiveCard; index: number }> = ({ card, index }) => {
    const Icon = card.icon;
    const flip = index % 2 !== 0;
    return (
        <m.div
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: (index % 2) * 0.08 }}
            className="bg-white border border-[#e8eaed] rounded-[32px] overflow-hidden hover:shadow-lg transition-shadow duration-300"
        >
            <div className={`grid grid-cols-1 lg:grid-cols-2 ${flip ? 'lg:[&>div:first-child]:order-2' : ''}`}>
                {/* Left: content */}
                <div className="p-8 md:p-12 flex flex-col justify-between">
                    <div>
                        <div className="flex items-start gap-4 mb-6">
                            <div
                                className="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 mt-0.5"
                                style={{ backgroundColor: card.accentBg, color: card.accentColor }}
                            >
                                <Icon size={24} strokeWidth={1.5} />
                            </div>
                            <div>
                                <h3 className="text-2xl font-medium text-[#121317] leading-snug">{card.title}</h3>
                                <p className="text-[0.9rem] mt-1" style={{ color: card.accentColor }}>{card.tagline}</p>
                            </div>
                        </div>
                        <p className="text-[0.9375rem] text-[#5f6368] leading-[1.75] mb-8">{card.description}</p>
                        <div className="flex flex-wrap gap-3 mb-8">
                            {card.keyFacts.map(f => (
                                <div key={f.label} className="flex flex-col px-4 py-2.5 rounded-2xl border border-[#e8eaed] bg-[#f8f9fa]">
                                    <span className="text-[0.7rem] font-medium text-[#80868b] uppercase tracking-wide">{f.label}</span>
                                    <span className="text-[0.9rem] font-medium text-[#121317]">{f.value}</span>
                                </div>
                            ))}
                        </div>
                        <Link
                            to={card.link}
                            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-medium text-white no-underline transition-all hover:opacity-90 hover:shadow-md text-[0.9375rem]"
                            style={{ backgroundColor: card.accentColor }}
                        >
                            {card.cta} <ArrowRight size={16} />
                        </Link>
                    </div>
                </div>
                {/* Right: use-cases panel */}
                <div
                    className="p-8 md:p-12 flex flex-col justify-center"
                    style={{ backgroundColor: card.accentBg + '55' }}
                >
                    <h4 className="text-[0.75rem] font-semibold uppercase tracking-widest mb-5" style={{ color: card.accentColor }}>
                        Common use cases
                    </h4>
                    <ul className="space-y-4 list-none p-0 m-0">
                        {card.useCases.map((uc, i) => (
                            <li key={i} className="flex items-start gap-3">
                                <CheckCircle size={16} className="shrink-0 mt-0.5" style={{ color: card.accentColor }} />
                                <span className="text-[0.9rem] text-[#3c4043] leading-snug">{uc}</span>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>
        </m.div>
    );
};

// ── Page ──────────────────────────────────────────────────────────────────────

const ToolsPage: React.FC = () => {
    const [randomInsights, setRandomInsights] = useState<Article[]>([]);

    useEffect(() => {
        const shuffled = [...allPosts].sort(() => 0.5 - Math.random());
        setRandomInsights(shuffled.slice(0, 3));
    }, []);

    return (
        <>
            <SEO {...seoConfig.tools} />
            <div className="min-h-screen bg-white font-brand text-[#202124]">
                <main>
                    {/* ── Hero ───────────────────────────────────────────────── */}
                    <header className="max-w-7xl mx-auto px-6 pt-16 pb-20 text-center">
                        <Typography variant="hero-title" as="h1" weight="light" className="mb-6 text-[#121317]">
                            Precision utilities.{' '}<span className="text-[#1a73e8]">Engineered for scale.</span>
                        </Typography>
                        <Typography variant="hero-subtitle" weight="normal" className="max-w-2xl mx-auto text-[#5f6368] mb-10">
                            Eight powerful tools for every project and professional workflow — all browser-based,
                            privately processed, and permanently free.
                        </Typography>
                        <div className="flex flex-wrap gap-4 justify-center">
                            <Link
                                to="/features"
                                className="inline-flex items-center gap-2 px-7 py-3 bg-[#1a73e8] text-white font-medium rounded-full no-underline hover:bg-[#1557b0] transition-colors shadow-sm"
                            >
                                See full platform <ArrowRight size={17} />
                            </Link>
                            <Link
                                to="/dev-hub"
                                className="inline-flex items-center gap-2 px-7 py-3 bg-white border border-[#dadce0] text-[#3c4043] font-medium rounded-full no-underline hover:bg-[#f8f9fa] transition-colors"
                            >
                                Developer Hub
                            </Link>
                        </div>
                    </header>

                    {/* ── Stat strip ─────────────────────────────────────────── */}
                    <section className="max-w-7xl mx-auto px-6 pb-20 border-b border-[#f1f3f4]">
                        <div className="flex justify-center gap-12 sm:gap-24 flex-wrap">
                            {[
                                { value: '8', label: 'Professional Tools' },
                                { value: '100%', label: 'Browser-Based' },
                                { value: '0', label: 'Account Required' },
                                { value: '∞', label: 'Free Forever' },
                            ].map(s => (
                                <div key={s.label} className="flex flex-col items-center text-center">
                                    <div className="text-[2.75rem] font-light text-[#121317] leading-none mb-2">{s.value}</div>
                                    <div className="text-[0.875rem] text-[#5f6368]">{s.label}</div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* ── Tool Deep-Dive Cards ────────────────────────────────── */}
                    <section className="max-w-7xl mx-auto px-6 py-20">
                        <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-6 mb-12">
                            <div className="max-w-2xl">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-9 h-9 rounded-xl flex items-center justify-center bg-[#e8f0fe] text-[#1a73e8]">
                                        <FileText size={18} strokeWidth={1.5} />
                                    </div>
                                    <span className="text-[0.8rem] font-semibold uppercase tracking-widest text-[#1a73e8]">Professional Tools</span>
                                </div>
                                <h2 className="text-3xl md:text-4xl font-light text-[#121317] mb-3">Eight tools for everyone</h2>
                                <p className="text-[#5f6368] text-lg leading-relaxed">
                                    Browser-based, private, and built for real workflows — from quick tasks to professional pipelines.
                                </p>
                            </div>
                        </div>
                        <div className="flex flex-col gap-6">
                            {toolCards.map((card, i) => (
                                <DeepDiveSection key={card.id} card={card} index={i} />
                            ))}
                        </div>
                    </section>

                    {/* ── Quality Traits ─────────────────────────────────────── */}
                    <section className="bg-white py-20 border-t border-[#f1f3f4]">
                        <div className="max-w-7xl mx-auto px-6">
                            <div className="bg-[#f8f9fa] rounded-[32px] p-8 md:p-16 grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                                <div className="bg-white border border-[#e8eaed] rounded-2xl p-8 flex flex-col items-center">
                                    <div className="w-14 h-14 bg-[#e8f0fe] rounded-2xl flex items-center justify-center mb-6">
                                        <Shield size={28} className="text-[#1a73e8]" />
                                    </div>
                                    <h3 className="text-xl font-medium mb-3 text-[#121317]">Enterprise-Grade Security</h3>
                                    <p className="text-[#5f6368] leading-relaxed">
                                        Advanced server-side processing with industry-standard encryption. Files are automatically and permanently deleted immediately after processing
                                    </p>
                                </div>
                                <div className="bg-white border border-[#e8eaed] rounded-2xl p-8 flex flex-col items-center">
                                    <div className="w-14 h-14 bg-[#e6f4ea] rounded-2xl flex items-center justify-center mb-6">
                                        <CheckCircle size={28} className="text-[#1e8e3e]" />
                                    </div>
                                    <h3 className="text-xl font-medium mb-3 text-[#121317]">No Sign-up Required</h3>
                                    <p className="text-[#5f6368] leading-relaxed">
                                        Immediate access to premium engineering tools. No credit cards, no emails, just professional utilities at your fingertips
                                    </p>
                                </div>
                                <div className="bg-white border border-[#e8eaed] rounded-2xl p-8 flex flex-col items-center">
                                    <div className="w-14 h-14 bg-[#fef9e7] rounded-2xl flex items-center justify-center mb-6">
                                        <Star size={28} className="text-[#f9ab00]" />
                                    </div>
                                    <h3 className="text-xl font-medium mb-3 text-[#121317]">Open & Transparent</h3>
                                    <p className="text-[#5f6368] leading-relaxed">
                                        Our algorithms are designed for clarity and precision. We believe in providing technical depth alongside simplicity
                                    </p>
                                </div>
                            </div>
                        </div>
                    </section>

                    {/* ── Engineering Guides ─────────────────────────────────── */}
                    <section className="max-w-7xl mx-auto px-6 py-24">
                        <div className="flex flex-col md:flex-row justify-between items-baseline mb-12 gap-4">
                            <div>
                                <h2 className="text-3xl font-light text-[#121317] mb-3">Engineering Guides</h2>
                                <p className="text-[#5f6368] text-lg">In-depth technical resources for modern engineering</p>
                            </div>
                            <Link
                                to="/insights/articles"
                                className="text-[#1a73e8] font-medium flex items-center gap-1 hover:underline group"
                            >
                                Browse all articles
                                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                            </Link>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            {randomInsights.map((post) => (
                                <Link
                                    key={post.id}
                                    to={`/insights/articles/${post.slug}`}
                                    className="group flex flex-col bg-white border border-[#dadce0] rounded-[32px] overflow-hidden hover:shadow-md transition-all duration-300 no-underline"
                                >
                                    <div className="aspect-[16/10] overflow-hidden bg-[#f8f9fa]">
                                        <OptimizedImage
                                            src={post.imageUrl}
                                            alt={post.title}
                                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                            cinematic
                                        />
                                    </div>
                                    <div className="p-6 flex flex-col flex-1">
                                        <span className="text-[0.75rem] font-bold text-[#1a73e8] uppercase tracking-wider mb-2">
                                            {post.category.replace(/-/g, ' ')}
                                        </span>
                                        <h3 className="text-xl font-medium text-[#121317] mb-3 line-clamp-2 group-hover:text-[#1a73e8] transition-colors">
                                            {post.title}
                                        </h3>
                                        <p className="text-[0.9rem] text-[#5f6368] line-clamp-2 flex-1">{post.excerpt}</p>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </section>
                </main>
            </div>
        </>
    );
};

export default ToolsPage;
